import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Link as ChakraLink,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Link as ReactRouterLink } from 'react-router-dom';
import DocumentUpload from './DocumentUpload';
import RecentlyUploaded from './RecentlyUploaded';
import { IUploadFile } from '../../interfaces';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import 'moment/dist/locale/de';
import DocumentService from '../../services/DocumentService.ts';
import { IDocumentListResponse } from '../../interfaces/Document/IDocumentListResponse.ts';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import AleaModal from '../../components/AleaModal/AleaModal.tsx';
import { TModalConfig } from '../../types/Modal/TModalConfig.ts';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';

const AddDocumentPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();

  // State for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState<IUploadFile[]>([]);

  // State for loading spinner
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // State for deletion spinner
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // State for modal
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<TModalConfig>({
    title: '',
    onConfirm: () => {},
    variant: 'confirm',
  });

  useEffect(() => {
    if (i18n.language) {
      // Set the locale for moment
      moment.locale(i18n.language);

      // Fetch documents for the recently uploaded section
      fetchDocuments();
    }
  }, [i18n.language]);

  /**
   * Handles adding new files to the upload list.
   * @param {IUploadFile[]} files - The files to be added.
   */
  const handleFilesAdded = async (files: IUploadFile[]) => {
    for (const file of files) {
      const fileId = uuidv4();
      setUploadedFiles((prevFiles) => [
        {
          ...file,
          id: fileId,
          isUploading: true,
          progress: 0,
        },
        ...prevFiles,
      ]);

      try {
        const response = await DocumentService.uploadDocument(
          file.fileObject!,
          (progressEvent) => {
            const total = progressEvent.total ?? progressEvent.loaded;
            const progress = Math.round((progressEvent.loaded * 100) / total);
            setUploadedFiles((prevFiles) =>
              prevFiles.map((f) => (f.id === fileId ? { ...f, progress } : f))
            );
          }
        );

        setUploadedFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  id: response.id,
                  isUploading: false,
                  progress: 100,
                  date: moment().format('DD/MM/YYYY'),
                  time: moment().fromNow(),
                }
              : f
          )
        );
      } catch (error) {
        console.error(`Failed to upload document: ${file.name}`, error);
        setUploadedFiles((prevFiles) =>
          prevFiles.filter((f) => f.id !== fileId)
        );
      }
    }
  };

  const fetchDocuments = async (page = 1) => {
    setIsLoading(true);
    try {
      const response: IDocumentListResponse =
        await DocumentService.getDocuments({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          page,
          per_page: 5,
          orderBy: 'created_at',
          sortOrder: 'desc',
        });

      setUploadedFiles(
        response.items.map((item) => ({
          id: item.id,
          name: item.name,
          date: moment(item.created_at).format('DD/MM/YYYY'),
          time: moment(item.created_at).fromNow(),
          isUploading: false,
          progress: 100,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the deletion of a file.
   * @param {string} id - The ID of the file to be deleted.
   */
  const handleDelete = async (id: string) => {
    try {
      // Close the modal
      setModalOpen(false);

      // Show the loading spinner
      setIsDeleting(true);

      // Delete the document
      await DocumentService.deleteDocument(id);

      // Hide the loading spinner
      setIsDeleting(false);

      // Remove the file from the list
      setUploadedFiles((prevFiles) =>
        prevFiles.filter((file) => file.id !== id)
      );

      // Show the success toast
      toast({
        title: t('fileDeleted'),
        description: t('fileDeletedSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to delete document', error);
    }
  };

  const confirmDeleteFile = async (documentId: string) => {
    // Set the modal configuration
    setModalConfig({
      title: t('confirmFileDelete'),
      onConfirm: async () => {
        await handleDelete(documentId);
      },
      variant: 'confirm',
    });

    // Open the modal
    setModalOpen(true);
  };

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        bg="white"
        borderRadius="20px"
        h="75"
      >
        <Heading size="lg" fontSize={25} fontWeight={500}>
          <Flex alignItems="center">
            <Flex w={75} justifyContent="center">
              <ChakraLink as={ReactRouterLink} to="/document-management">
                <ChevronLeftIcon />
              </ChakraLink>
            </Flex>
            <Flex fontSize={24} fontWeight={400}>
              {t('newDocument')}
            </Flex>
          </Flex>
        </Heading>
      </Flex>

      <Box px="75px" py="50px" bg="white" borderRadius="20px">
        <DocumentUpload onFilesAdded={handleFilesAdded} />

        {isLoading ? (
          <Flex align="center" justifyContent="center" mt="15px">
            <Spinner />
          </Flex>
        ) : (
          <RecentlyUploaded
            files={uploadedFiles}
            onDelete={confirmDeleteFile}
          />
        )}
      </Box>

      <AleaModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={modalConfig.title}
        description={modalConfig.description}
        onConfirm={modalConfig.onConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant={modalConfig.variant}
      />

      {isDeleting && <AleaLoader />}
    </Box>
  );
};

export default AddDocumentPage;
